import discountMonthGetter from '../valueGetters/discountMonthGetter';
import currencyFormatter from '../formatters/priceFormatter';
import percentFormatter from '../formatters/percentFormatter';
import { getClassForAgGridCell } from '../../../helpers/cellHelpers';
import {
  Nullable,
  PricingSheetRow,
  RoundingBasis,
} from '../../../../../shared/types';
import NumericEditorV2 from '../Editors/NumericEditorV2';
import {
  CellClassParams,
  CellStyle,
  ICellEditorParams,
  ValueGetterParams,
} from 'ag-grid-community';
import proposalMonthPriceGetter from '../valueGetters/proposalMonthPriceGetter';
import { monthPriceGetter } from '../valueGetters/monthPriceGetter';
import { GridColumnProps } from './ColumnsBase';
import comparisonPriceValueGetter from '../valueGetters/comparisonPriceValueGetter';
import monthPricePercentGetter from '../valueGetters/monthPricePercentGetter';
import { getCatClassColDef } from '../helpers/getCatClassColDef';
import PriceRenderer from '../Renderers/PriceRenderer';

interface OptionalColumnProps {
  roundingBasisSheet: RoundingBasis;
  handleUpdateRowValue: (row: PricingSheetRow) => void;
  isEditingDisabled: boolean;
}

const ColumnsMonth = ({
  classes,
  gridGroupingValues,
  optionalColumnProps,
}: GridColumnProps<OptionalColumnProps>) => {
  const { handleUpdateRowValue, isEditingDisabled } =
    optionalColumnProps;

  /*
   * for some reason right border does not apply if we pass it from cellClass
   * therefore we define borders here in cellStyles and only get colors from cellClass
   * */
  const columns = [
    ...getCatClassColDef(gridGroupingValues),
    {
      field: 'proposalMonthPrice',
      headerName: 'EhdotusKKHinta',
      filter: 'agNumberColumnFilter',
      valueGetter: proposalMonthPriceGetter,
      valueFormatter: currencyFormatter,
      headerTooltip: 'Työkalun ehdottama hinta kuukausihinnaksi',
      cellStyle: {
        backgroundColor: '#fafafa',
        borderLeft: '1px solid #ccc',
      } as CellStyle,
    },
    {
      field: 'discountMonth',
      headerName: 'Alennus% (KK)',
      filter: 'agNumberColumnFilter',
      valueGetter: discountMonthGetter,
      valueFormatter: percentFormatter,
      editable: (params: { data: PricingSheetRow }) => {
        if (isEditingDisabled) {
          return false;
        }
        return !params.data.showByOfferText;
      },
      headerTooltip: 'Kuukausihinta verrattuna ehdotushintaan',
      cellEditor: NumericEditorV2,
      cellEditorParams: {
        handleUpdateRowValue,
      },
      cellClass: (params: CellClassParams) => {
        return getClassForAgGridCell(
          params.data,
          classes,
          'discountMonth',
        );
      },
      cellStyle: () => {
        return {
          color: '#000',
          borderLeft: '1px solid #ccc',
        } as CellStyle;
      },
    },
    {
      field: 'monthPrice',
      headerName: 'KKHinta',
      filter: 'agNumberColumnFilter',
      valueGetter: (params: ValueGetterParams<PricingSheetRow>) =>
        monthPriceGetter(
          params,
          optionalColumnProps.roundingBasisSheet,
        ),
      valueFormatter: currencyFormatter,
      editable: (params: { data: PricingSheetRow }) => {
        if (isEditingDisabled) {
          return false;
        }
        return !params.data.showByOfferText;
      },
      headerTooltip: 'Tuotteen kuukausihinta',
      cellEditor: NumericEditorV2,
      cellEditorParams: {
        handleUpdateRowValue,
      },
      cellClass: (params: CellClassParams) => {
        return getClassForAgGridCell(
          params.data,
          classes,
          'monthPrice',
        );
      },
      cellRenderer: PriceRenderer,
      cellRendererParams: (
        params: ICellEditorParams<PricingSheetRow>,
      ) => {
        return {
          minPrice: params.data.minMonthPrice,
        };
      },
      cellStyle: () => {
        return {
          color: '#000',
          borderLeft: '1px solid #ccc',
          borderRight: '1px solid #ccc',
        } as CellStyle;
      },
    },
    {
      field: 'comparisonMonthPrice',
      headerName: 'VRThintaKK',
      filter: 'agNumberColumnFilter',
      valueGetter: (params: ValueGetterParams<PricingSheetRow>) =>
        comparisonPriceValueGetter(
          params,
          optionalColumnProps.roundingBasisSheet,
          params.data?.comparisonMonthPrice,
        ),
      valueFormatter: currencyFormatter,
      headerTooltip:
        'Tuotteen kuukausihinta valitussa vertailuhinnastossa',
      cellStyle: {
        backgroundColor: '#fafafa',
      },
    },
    {
      field: 'comparisonMonthPricePercent',
      headerName: 'VRTero%KK',
      filter: 'agNumberColumnFilter',
      valueGetter: (params: ValueGetterParams<PricingSheetRow>) =>
        monthPricePercentGetter(
          params,
          params.data?.comparisonMonthPrice,
        ),
      valueFormatter: (params: {
        value: Nullable<number>;
        data: PricingSheetRow | undefined;
      }) => {
        if (!params.value) return '';
        return `${params.value} %`;
      },
      cellStyle: {
        backgroundColor: '#fafafa',
        borderLeft: '1px solid #ccc',
      } as CellStyle,
      suppressColumnsToolPanel: false,
      hide:
        localStorage.getItem(
          'columnVisibility_comparisonMonthPricePercent',
        ) !== 'true',
    },
  ];
  return columns;
};

export default ColumnsMonth;
